import { useContext, useEffect, useId, useMemo } from 'react'
import debounce from 'lodash.debounce'
import { DevPanelDispatchContext } from './DevPanelContext'
import noop from 'lib/function/noop'
import useStateWithSetters from 'hooks/useStateWithSetters'

export function useReportContextToDevPanel(name: string, value: any) {
  const devPanelDispatch = useContext(DevPanelDispatchContext)
  const id = useId()

  const debouncedDevPanelDispatch = useMemo(() => (
    devPanelDispatch === noop ? noop : debounce(devPanelDispatch, 200)
  ), [devPanelDispatch])

  useEffect(() => {
    debouncedDevPanelDispatch({
      type: 'reportContext',
      context: { id, name, value },
    })
  }, [debouncedDevPanelDispatch, id, name, value])

  useEffect(() => {
    return () => {
      devPanelDispatch({
        type: 'removeContext',
        id,
      })
    }
  }, [devPanelDispatch, id])
}

export function useSessionPersistedTabs<const S extends string>(tabIds: Array<S>, storageKey: string, defaultTab?: S) {
  const stateReturnValue = useStateWithSetters(tabIds, () => {
    try {
      const saved = sessionStorage.getItem(storageKey) as typeof tabIds[number] | null
      return saved ?? defaultTab ?? tabIds[0]
    } catch {
      return defaultTab ?? tabIds[0]
    }
  })

  const [currentTabId] = stateReturnValue

  useEffect(() => {
    try {
      sessionStorage.setItem(storageKey, currentTabId)
    } catch {}
  }, [currentTabId, storageKey])

  return stateReturnValue
}
